<template>
    <table class="table details is-fullwidth">
        <thead v-if="!totalsOnly">
            <tr>
                <th>{{ $t('Article / Description') }}</th>
                <th style="min-width: 90px" class="has-text-centered">{{ $t('Qty.') }}</th>
                <th style="min-width: 90px" class="has-text-right">{{ $t('Unit price') }}</th>
                <th style="min-width: 90px" class="has-text-right">{{ $t('Price') }}</th>
            </tr>
        </thead>

        <tbody v-if="!totalsOnly">
            <tr v-for="detail in lines" :key="detail.id">
                <td>
                    <b v-if="detail.name">
                        {{ detail.name }}

                        <product-link v-if="linkProducts" :product-id="detail.product_id" class="ml-1" />
                    </b>
                    <template v-if="detail.description">
                        <br v-if="detail.name" />
                        <p style="white-space: pre-wrap">{{ detail.description }}</p>
                    </template>
                </td>
                <td class="has-text-centered">{{ $filters.quantity(detail.quantity) }}</td>
                <td class="has-text-right">{{ $filters.money(unitPrice(detail)) }}</td>
                <td class="has-text-right">{{ $filters.money(lineTotal(detail)) }}</td>
            </tr>
        </tbody>

        <tfoot v-if="!linesOnly">
            <tr>
                <td colspan="3" class="has-text-right has-text-weight-bold">{{ $t('Subtotal') }}</td>
                <td class="has-text-right has-text-weight-bold">{{ $filters.money(subtotal) }}</td>
            </tr>

            <tr v-if="shipping">
                <td colspan="3" class="has-text-right">{{ $t('Shipping') }}</td>
                <td class="has-text-right">{{ $filters.money(shipping) }}</td>
            </tr>

            <tr v-if="discount > 0">
                <td colspan="3" class="has-text-right">{{ $t('Discount') }}</td>
                <td class="has-text-right">-{{ $filters.money(discount) }}</td>
            </tr>

            <tr v-for="tax in taxes || []" :key="tax.id">
                <td colspan="3" class="has-text-right">{{ tax.name }} ({{ tax.rate }}%)</td>
                <td class="has-text-right">{{ $filters.money(tax.amount) }}</td>
            </tr>

            <!-- A document billed in another currency shows both totals: the one
                 the customer owes, and the one the books are kept in. -->
            <template v-if="showsBothCurrencies">
                <tr>
                    <td colspan="3" class="has-text-right total">{{ $t('Total') }} ({{ currencyCode }})</td>
                    <td class="has-text-right total">{{ $filters.money(total, currencyCode) }}</td>
                </tr>
                <tr>
                    <td colspan="3" class="has-text-right total">{{ $t('Total') }} ({{ orgStore.currency_code }})</td>
                    <td class="has-text-right total">
                        {{ $filters.money(total * exchangeRate, orgStore.currency_code) }}
                    </td>
                </tr>
            </template>
            <tr v-else>
                <td colspan="3" class="has-text-right total">{{ $t('Total') }}</td>
                <td class="has-text-right total">{{ $filters.money(total) }}</td>
            </tr>

            <!-- Paid / balance rows differ per screen: an invoice preview shows
                 "Paid" then "Balance Due", the customer portal shows a single
                 "Balance" in red. Each caller keeps its own. -->
            <slot name="footer-extra" />
        </tfoot>
    </table>
</template>

<script setup>
import { computed } from 'vue';
import { useOrgStore } from '@/stores/org';

/**
 * The line table shared by every screen that presents a document's items:
 * the invoice preview, the customer portal, and a work order's Billing tab.
 *
 * It was copy-pasted between the first two before this existed, which is how
 * the third screen ended up with a plainer table of its own — no subtotal, no
 * taxes, no total.
 */
const props = defineProps({
    lines: { type: Array, default: () => [] },
    subtotal: { type: [Number, String], default: 0 },
    shipping: { type: [Number, String], default: 0 },
    discount: { type: [Number, String], default: 0 },
    /** `[{ id, name, rate, amount }]` — from the server, or summarizeTaxes(). */
    taxes: { type: Array, default: () => [] },
    total: { type: [Number, String], default: 0 },
    /** The portal has no product pages to link to. */
    linkProducts: { type: Boolean, default: false },
    currencyCode: { type: String, default: null },
    exchangeRate: { type: [Number, String], default: 1 },
    /** Render the rows alone — for a screen that shows its own editable footer. */
    linesOnly: { type: Boolean, default: false },
    /**
     * Render the totals alone — for a screen that shows its own editable rows
     * above. Without this, the column headers reappear between the last input
     * row and the subtotal, heading a table that has no rows.
     */
    totalsOnly: { type: Boolean, default: false }
});

const orgStore = useOrgStore();

const showsBothCurrencies = computed(() => props.currencyCode && props.currencyCode !== orgStore.currency_code);

// The portal receives some documents with `price` where others carry
// `unit_price`, and without a precomputed line total.
//
// `??` rather than `||`, which is what the portal used to do: a line priced at
// zero — something thrown in free — fell through to the catalogue price and was
// shown as billed. Zero is a price; only a missing one falls back.
function unitPrice(detail) {
    return detail.unit_price ?? detail.price;
}

function lineTotal(detail) {
    return detail.total ?? (detail.quantity || 0) * (unitPrice(detail) || 0);
}
</script>
